import { C } from '../../lib/landingColors'
import {
  MarketingHeading,
  MarketingSubheading,
  MarketingParagraph,
  MarketingList,
  MarketingListItem,
  MarketingLink,
} from '../../components/MarketingShell'

/**
 * The English Terms & Conditions — the BINDING version (clause 16).
 *
 * Lifted out of `pages/TermsPage.tsx` unchanged on 2026-09-09 when the page
 * became a pair; the only edit to the text itself is the new clause 16, which
 * is what licenses the German sibling to exist at all. Everything above it is
 * byte-for-byte what it was: a terms page gets quoted back in a support thread
 * or a DPA negotiation, so a sentence that moved during a refactor is a change
 * nobody reviewed.
 *
 * Clause 16 was APPENDED rather than inserted near the top on purpose —
 * inserting it would renumber sections 1-15 and break every cross-reference in
 * the body (5.5, 8, 9, 11, 12) and in the privacy policy. The German version
 * carries the same clause 16 plus a notice above section 1, because that is the
 * reader who needs to meet it first.
 */
export function TermsBodyEn() {
  return (
    <>
      {/* ── 1. Service Description ──────────────────────────────── */}
      <section>
        <MarketingHeading>1. Service Description</MarketingHeading>
        <MarketingParagraph>
          StudioFlow is a cloud-based studio management platform designed for
          professional photography and videography studios. It provides tools for
          project management, client management, scheduling, invoicing, client
          gallery delivery, team collaboration, and related workflows. The
          platform is operated by StudioFlow ("we", "us", "our") and is
          available via web browser and, where applicable, mobile interfaces.
        </MarketingParagraph>
      </section>

      {/* ── 2. Account & Access ─────────────────────────────────── */}
      <section>
        <MarketingHeading>2. Account &amp; Access</MarketingHeading>
        <MarketingParagraph>
          Each user account is personal and may not be shared. You are
          responsible for maintaining the confidentiality of your login
          credentials and for all activity that occurs under your account.
          You must notify us immediately of any unauthorised use. We reserve
          the right to disable any account if we reasonably believe credentials
          have been compromised.
        </MarketingParagraph>
        <MarketingParagraph>
          You may not create an account on behalf of another person or entity
          without their authorisation, nor create multiple accounts for the
          purpose of circumventing plan limits, fair use thresholds, or
          restrictions imposed under these Terms.
        </MarketingParagraph>
      </section>

      {/* ── 3. Data Ownership ───────────────────────────────────── */}
      <section>
        <MarketingHeading>3. Data Ownership</MarketingHeading>
        <MarketingParagraph>
          You retain full ownership of all content you upload or create on the
          platform, including but not limited to: photographs, videos, documents,
          client data, invoices, contracts, and project information. We do not
          claim any intellectual property rights over your content. The platform
          stores and processes your data solely to provide and improve the
          service.
        </MarketingParagraph>
        <MarketingParagraph>
          You grant us a limited, non-exclusive licence to host, store, cache,
          and transmit your content as reasonably necessary to operate the
          service (e.g. generating thumbnails, delivering client galleries,
          creating backups).
        </MarketingParagraph>
      </section>

      {/* ── 4. Subscriptions, Billing & Refunds ─────────────────── */}
      <section>
        <MarketingHeading>4. Subscriptions, Billing &amp; Refunds</MarketingHeading>
        <MarketingParagraph>
          StudioFlow offers subscription plans with different storage,
          team-member, and feature allowances. Plans are billed monthly or
          annually in Swiss Francs (CHF) via our payment processor, Stripe.
          All prices are inclusive of applicable Swiss VAT (MwSt) unless
          stated otherwise.
        </MarketingParagraph>
        <MarketingParagraph>
          Free trial accounts have full access to all features for 30 days.
          At the end of the trial period, you must select a paid plan to
          continue using the platform. If no plan is selected, your account
          will be downgraded and access to certain features will be restricted
          until a plan is activated; your data will be preserved for a minimum
          of 60 days after the trial expires.
        </MarketingParagraph>
        <MarketingParagraph>
          Subscriptions renew automatically at the end of each billing cycle
          unless cancelled beforehand. You may cancel or change your plan at
          any time through the Settings page or via the Stripe customer portal.
          Cancellations take effect at the end of the current billing period.
        </MarketingParagraph>
        <MarketingParagraph>
          Add-on services (e.g. storage extensions) are billed as separate
          recurring charges and may be managed independently of your main
          subscription.
        </MarketingParagraph>
        <MarketingParagraph>
          Refunds are handled on a case-by-case basis. If you believe you
          have been charged in error, please contact us within 14 days of the
          charge.
        </MarketingParagraph>
      </section>

      {/* ── 5. Fair Use & Storage Policy ─────────────────────────── */}
      <section>
        <MarketingHeading>5. Fair Use &amp; Storage Policy</MarketingHeading>
        <MarketingParagraph>
          StudioFlow is designed for professional photography and videography
          studio operations. All plan limits — including storage, team
          members, and API access — are subject to this Fair Use Policy.
        </MarketingParagraph>

        <MarketingSubheading>5.1 Storage</MarketingSubheading>
        <MarketingParagraph>
          Each subscription plan includes a base storage allowance. Storage is
          measured as the sum of all files stored on the platform, including
          gallery photos, chat attachments, and uploaded documents. Agency
          plans may purchase additional storage in 1 TB increments. Files you
          delete from the platform are released from your quota promptly and
          then permanently purged from object storage on our normal retention
          schedule.
        </MarketingParagraph>

        <MarketingSubheading>5.2 Client gallery delivery</MarketingSubheading>
        <MarketingParagraph>
          Client gallery viewing, album downloads, and team collaboration are
          included in all plans at no additional metered cost. StudioFlow
          delivers gallery content through a content delivery network (CDN)
          whose bandwidth is not separately billed under current pricing.
          We reserve the right to introduce metering, overage charges, or
          throttling in the future if a specific organisation's delivery
          volume materially diverges from normal studio operations; any such
          change affecting existing plans will be communicated in advance in
          accordance with Section 12.
        </MarketingParagraph>

        <MarketingSubheading>5.3 Prohibited patterns</MarketingSubheading>
        <MarketingParagraph>You agree not to:</MarketingParagraph>
        <MarketingList>
          <MarketingListItem>
            Use the platform for general-purpose file storage, backup, or
            archival unrelated to photography or videography studio operations
          </MarketingListItem>
          <MarketingListItem>
            Engage in repetitive upload-and-delete cycles designed to
            circumvent storage limits or to exploit infrastructure billing
            structures
          </MarketingListItem>
          <MarketingListItem>
            Use automated tools, scripts, or bots to upload, download, or
            delete content in bulk outside of the platform's intended
            interfaces
          </MarketingListItem>
          <MarketingListItem>
            Share gallery download links publicly or facilitate mass
            redistribution of content in a manner inconsistent with normal
            client delivery
          </MarketingListItem>
          <MarketingListItem>
            Create multiple organisations or accounts to circumvent plan
            limits
          </MarketingListItem>
        </MarketingList>

        <MarketingSubheading>5.4 Monitoring &amp; enforcement</MarketingSubheading>
        <MarketingParagraph>
          We monitor aggregate usage patterns to ensure fair access for all
          users. We do not inspect the content of your files. If we detect
          usage patterns that appear inconsistent with normal studio
          operations, we will contact the organisation owner to discuss the
          situation before taking any action. If a pattern of abuse continues
          after notice, we reserve the right to throttle access, apply
          overage charges, or suspend the organisation as described in
          Section 9.
        </MarketingParagraph>

        <MarketingSubheading>5.5 Photo &amp; video storage is not a backup service</MarketingSubheading>
        <MarketingParagraph>
          StudioFlow stores the master files you upload in cloud object
          storage and serves them to clients and your team. This is
          <em> operational</em> storage, not an archival backup. While we
          take commercially reasonable measures to protect data at rest
          (including redundancy and versioning at the storage-provider
          level — see Section 8), we do not guarantee against permanent
          loss of uploaded media in events such as catastrophic provider
          failure, regional disaster, account compromise, or accidental
          deletion not recoverable within the provider's retention window.
        </MarketingParagraph>
        <MarketingParagraph>
          <strong>You are solely responsible for maintaining your own
          independent backup of original RAW files and any other master
          assets you consider irreplaceable.</strong> We strongly recommend
          keeping at least one offline or geographically separate copy of
          such files under your direct control. Our liability for loss of
          uploaded media is limited as set out in Section 11 and does not
          exceed the amounts stated there, regardless of cause.
        </MarketingParagraph>
      </section>

      {/* ── 6. Acceptable Use ───────────────────────────────────── */}
      <section>
        <MarketingHeading>6. Acceptable Use</MarketingHeading>
        <MarketingParagraph>In addition to the Fair Use Policy above, you agree not to:</MarketingParagraph>
        <MarketingList>
          <MarketingListItem>Upload, store, or distribute any illegal, defamatory, or
            infringing content</MarketingListItem>
          <MarketingListItem>Upload content that you do not have the right to store or
            distribute</MarketingListItem>
          <MarketingListItem>Resell, sublicense, or redistribute access to the platform</MarketingListItem>
          <MarketingListItem>Use automated tools to scrape, crawl, or extract data from the
            platform, or to reverse-engineer any part of the service</MarketingListItem>
          <MarketingListItem>Attempt to gain unauthorised access to other users' accounts,
            organisations, or data</MarketingListItem>
          <MarketingListItem>Use the platform in any way that could damage, disable,
            overburden, or impair the service or interfere with other
            users' access</MarketingListItem>
          <MarketingListItem>Introduce viruses, malware, or other harmful code</MarketingListItem>
          <MarketingListItem>Transmit unsolicited communications (spam) via the platform's
            messaging or email features</MarketingListItem>
        </MarketingList>
      </section>

      {/* ── 7. Intellectual Property ─────────────────────────────── */}
      <section>
        <MarketingHeading>7. Intellectual Property</MarketingHeading>
        <MarketingParagraph>
          The platform's software, design, logos, and documentation are the
          intellectual property of StudioFlow. Your content remains your
          property. This agreement does not transfer any intellectual property
          rights between the parties except as explicitly stated.
        </MarketingParagraph>
      </section>

      {/* ── 8. Data Handling & Security ──────────────────────────── */}
      <section>
        <MarketingHeading>8. Data Handling &amp; Security</MarketingHeading>
        <MarketingParagraph>
          Application data (database, application servers) is hosted in the
          European Union (Hetzner, Germany). Photo and video files are
          stored on Backblaze B2 object storage in an EU region and
          delivered to browsers through the Cloudflare content delivery
          network. All data in transit is encrypted via TLS. Database
          backups are performed daily. Photo and video files are protected
          at the storage-provider level through bucket versioning;
          however, as stated in Section 5.5, this operational storage is
          not a substitute for your own independent archival backup of
          master assets.
        </MarketingParagraph>
        <MarketingParagraph>
          We implement commercially reasonable technical and organisational
          measures to protect your data from unauthorised access, alteration,
          or destruction. However, no method of electronic transmission or
          storage is 100% secure and we cannot guarantee absolute security.
        </MarketingParagraph>
        <MarketingParagraph>
          For full details on how we collect, process, and handle your data,
          please refer to our{' '}
          <MarketingLink to="/privacy">Privacy Policy</MarketingLink>.
        </MarketingParagraph>
      </section>

      {/* ── 9. Suspension & Termination ──────────────────────────── */}
      <section>
        <MarketingHeading>9. Suspension &amp; Termination</MarketingHeading>

        <MarketingSubheading>9.1 By you</MarketingSubheading>
        <MarketingParagraph>
          You may cancel your subscription and request account deletion at any
          time through the Settings page. Organisation owners can delete their
          entire organisation and all associated data. Upon deletion, we will
          permanently remove your data within 30 days, except where retention
          is required by law.
        </MarketingParagraph>

        <MarketingSubheading>9.2 By us</MarketingSubheading>
        <MarketingParagraph>
          We reserve the right to suspend or terminate your account or
          organisation if:
        </MarketingParagraph>
        <MarketingList>
          <MarketingListItem>You violate these Terms &amp; Conditions, the Fair Use Policy, or
            the Acceptable Use Policy</MarketingListItem>
          <MarketingListItem>Your usage is determined to involve unlawful, prohibited,
            abnormal, or abusive activity</MarketingListItem>
          <MarketingListItem>Your account has been inactive for more than 12 consecutive
            months on a free or expired trial plan</MarketingListItem>
          <MarketingListItem>Payment for your subscription fails repeatedly and remains
            unresolved after notification</MarketingListItem>
        </MarketingList>
        <MarketingParagraph>
          Where practicable, we will provide at least 14 days' written notice
          to the organisation owner before suspension or termination, except
          in cases of severe abuse, legal obligation, or risk to other users
          or infrastructure, where we may act immediately.
        </MarketingParagraph>

        <MarketingSubheading>9.3 Effect of termination</MarketingSubheading>
        <MarketingParagraph>
          Upon termination or suspension, your right to use the service ceases
          immediately. We may deactivate or delete your account and all
          associated data. For accounts terminated by us for policy violations,
          we are not obligated to provide a copy of your data, though we will
          make reasonable efforts to allow data export where circumstances
          permit. Sections of these Terms that by their nature should survive
          termination (including Data Ownership, Limitation of Liability,
          Indemnification, and Governing Law) shall survive.
        </MarketingParagraph>
      </section>

      {/* ── 10. Indemnification ──────────────────────────────────── */}
      <section>
        <MarketingHeading>10. Indemnification</MarketingHeading>
        <MarketingParagraph>
          You agree to indemnify, defend, and hold harmless StudioFlow and
          its officers, directors, employees, and agents from and against any
          claims, liabilities, damages, losses, and expenses (including
          reasonable legal fees) arising out of or related to: (a) your use
          of the platform, (b) your violation of these Terms, (c) your
          violation of any third-party rights including intellectual property
          rights, or (d) any content you upload to or transmit through the
          platform.
        </MarketingParagraph>
      </section>

      {/* ── 11. Limitation of Liability ──────────────────────────── */}
      <section>
        <MarketingHeading>11. Limitation of Liability</MarketingHeading>
        <MarketingParagraph>
          The platform is provided "as is" without warranties of any kind,
          whether express or implied, including but not limited to
          merchantability, fitness for a particular purpose, and
          non-infringement. We do not guarantee uninterrupted or error-free
          service availability.
        </MarketingParagraph>
        <MarketingParagraph>
          To the maximum extent permitted by applicable law, we shall not be
          liable for any indirect, incidental, special, consequential, or
          punitive damages arising from or related to your use of the
          platform, including but not limited to loss of data, loss of
          revenue, or business interruption. Our total aggregate liability
          for any and all claims shall not exceed the amount you have paid
          for the service in the twelve (12) months immediately preceding the
          event giving rise to the claim.
        </MarketingParagraph>
      </section>

      {/* ── 12. Changes to Terms ─────────────────────────────────── */}
      <section>
        <MarketingHeading>12. Changes to Terms</MarketingHeading>
        <MarketingParagraph>
          We may update these Terms &amp; Conditions from time to time. For
          material changes, we will provide at least 30 days' notice via
          email to the organisation owner. We will also update the "Last
          updated" date at the top of this page. Continued use of the
          platform after the effective date of changes constitutes your
          acceptance of the updated terms. If you do not agree with the
          changes, you may cancel your subscription and delete your account
          before the changes take effect.
        </MarketingParagraph>
      </section>

      {/* ── 13. Governing Law & Jurisdiction ─────────────────────── */}
      <section>
        <MarketingHeading>13. Governing Law &amp; Jurisdiction</MarketingHeading>
        <MarketingParagraph>
          These Terms are governed by and construed in accordance with the
          laws of Switzerland, without regard to conflict-of-law principles.
          Any disputes arising from or in connection with these Terms shall
          be subject to the exclusive jurisdiction of the courts of Zurich,
          Switzerland.
        </MarketingParagraph>
      </section>

      {/* ── 14. Severability ──────────────────────────────────────── */}
      <section>
        <MarketingHeading>14. Severability</MarketingHeading>
        <MarketingParagraph>
          If any provision of these Terms is found to be invalid or
          unenforceable by a court of competent jurisdiction, the remaining
          provisions shall remain in full force and effect. The invalid
          provision shall be replaced with a valid provision that most
          closely reflects the original intent.
        </MarketingParagraph>
      </section>

      {/* ── 15. Contact ──────────────────────────────────────────── */}
      <section>
        <MarketingHeading>15. Contact</MarketingHeading>
        <MarketingParagraph>
          For any questions regarding these Terms &amp; Conditions, please
          contact us at{' '}
          <MarketingLink to="mailto:legal@studioflow.ch">legal@studioflow.ch</MarketingLink>
          .
        </MarketingParagraph>
      </section>

      {/* ── 16. Language ─────────────────────────────────────────── */}
      <section>
        <MarketingHeading>16. Language</MarketingHeading>
        <MarketingParagraph>
          These Terms &amp; Conditions are published in English and in German.
          The English version is the binding one; the German version is
          provided for convenience only. In the event of any conflict,
          inconsistency, or difference of interpretation between the two, the
          English version prevails. The German version is available at{' '}
          <MarketingLink to="/de/terms">/de/terms</MarketingLink>.
        </MarketingParagraph>
      </section>

      {/* The footer carries this link too now, but the cross-link between the
          two legal documents stays where a reader at the end of one expects it. */}
      <div className="mt-12 pt-6 text-sm" style={{ borderTop: `1px solid ${C.border}` }}>
        <MarketingLink to="/privacy">Privacy Policy</MarketingLink>
      </div>
    </>
  )
}
